import React, { FC, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Menu, MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import Flatpickr from './Flatpickr';

interface IProps {
  value: number;
  anchorEl: HTMLElement;
  onSave: (time: number) => void;
  onClose: () => void;
  minDate?: string;
  maxDate?: string;
  enableTime?: boolean;
  enableClear?: boolean;
}

const useStyles = makeStyles(theme => ({
  wrapper: {
    margin: theme.spacing(2),
  },
}));

const DateTime: FC<IProps> = props => {
  const classes = useStyles();
  const { anchorEl, onClose, onSave, value, enableClear, ...flatPickrOptions } = props;
  const { enableTime, minDate, maxDate } = flatPickrOptions;

  const [tempTime, setTempTime] = useState(null);
  const { t } = useTranslation();

  const time = moment.unix(tempTime || value).toDate();
  const options = {
    minDate: minDate || (maxDate ? null : 'today'),
    maxDate,
    inline: true,
    dateFormat: 'U',
  };

  function handleDateChange(selectedDates: Date[]) {
    const newMoment = toRoundedMoment(selectedDates[0]);
    if (tempTime) {
      const tempMoment = moment.unix(tempTime);
      newMoment.hours(tempMoment.hours());
      newMoment.minutes(tempMoment.minutes());
    }
    const newTime = boundedUnixTimestamp(newMoment.unix());
    setTempTime(newTime);
  }

  function handleDateSave() {
    onSave(tempTime || value);
  }

  function handleClose(event: KeyboardEvent) {
    event.key === 'Tab' ? event.stopPropagation() : onClose();
  }

  function handleTimeUpdate(hours: number, minutes: number, am: string) {
    let hours24 = hours;
    if (hours < 12 && am === 'PM') {
      hours24 += 12;
    } else if (hours === 12 && am === 'AM') {
      hours24 = 0;
    }
    let newTime = moment
      .unix(tempTime)
      .hours(hours24)
      .minutes(minutes)
      .unix();
    newTime = boundedUnixTimestamp(newTime);
    setTempTime(newTime);
  }

  function renderSaveButton() {
    return (
      <Button
        fullWidth
        disabled={!tempTime && !value}
        type="submit"
        size="large"
        variant="contained"
        color="primary"
        onClick={handleDateSave}>
        {t('common:save')}
      </Button>
    );
  }

  function renderSave() {
    return <div className={classes.wrapper}>{renderSaveButton()}</div>;
  }

  function renderClearAndSave() {
    return (
      <div className={classes.wrapper}>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <Button onClick={() => onSave(null)}>{t('common:clear')}</Button>
          </Grid>
          <Grid item>{renderSaveButton()}</Grid>
        </Grid>
      </div>
    );
  }

  function toRoundedMoment(date: Date) {
    const m = moment(date);
    let minute = m.minutes();
    while (minute % 5) {
      minute++;
    }
    m.minutes(minute);
    return m;
  }

  function roundedBound(val: string | Date) {
    const date = val === 'today' ? new Date() : new Date(val);
    return toRoundedMoment(date).unix();
  }

  function boundedUnixTimestamp(unix: number) {
    if (options.minDate) {
      const minTime = roundedBound(options.minDate);
      unix = Math.max(unix, minTime);
    }
    if (options.maxDate) {
      const maxTime = roundedBound(options.maxDate);
      unix = Math.min(unix, maxTime);
    }
    return unix;
  }

  function renderTimePicker() {
    if (!tempTime || !enableTime) {
      return null;
    }

    const indices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    const hourList = indices.map(n => n + 1);
    const minuteList = indices.map(n => n * 5);

    const tempMoment = toRoundedMoment(new Date(tempTime * 1000));
    const tempMinute = tempMoment.minutes();
    const tempHour = tempMoment.hours() % 12 || 12;
    const tempAm = tempMoment.hours() >= 12 ? 'PM' : 'AM';

    return (
      <Box pl={1} pr={1}>
        <Grid container>
          <Grid item xs={4}>
            <Select
              fullWidth
              value={tempHour}
              onChange={event =>
                handleTimeUpdate(event.target.value as number, tempMinute, tempAm)
              }>
              {hourList.map(hour => (
                <MenuItem key={hour} value={hour}>
                  {hour}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <Select
              fullWidth
              value={tempMinute}
              onChange={event => handleTimeUpdate(tempHour, event.target.value as number, tempAm)}>
              {minuteList.map(minute => (
                <MenuItem key={minute} value={minute}>
                  {minute < 10 ? `0${minute}` : minute}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <Select
              fullWidth
              value={tempAm}
              onChange={event =>
                handleTimeUpdate(tempHour, tempMinute, event.target.value as string)
              }>
              <MenuItem value="AM">AM</MenuItem>
              <MenuItem value="PM">PM</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose} data-testid="datePicker">
      <div>
        <Flatpickr value={time} onChange={handleDateChange} options={options} />
        {renderTimePicker()}
        {enableClear ? renderClearAndSave() : renderSave()}
      </div>
    </Menu>
  );
};

DateTime.defaultProps = {
  enableTime: false,
  enableClear: false,
};

export default DateTime;
